import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { getDashboardStats } from "../../lib/supabase/dashboard";

import { FaBoxesStacked, FaPlus } from "react-icons/fa6";
import { FaBoxOpen } from "react-icons/fa";
import { RiCalendarScheduleFill } from "react-icons/ri";
import { MdManageAccounts } from "react-icons/md";
import { FiCheckCircle, FiClock, FiAlertTriangle, FiTrendingUp } from 'react-icons/fi';

import Skeleton from 'react-loading-skeleton';
import { hasPermission } from "../../lib/permissions";
import { Truck } from "lucide-react";
import { TiTick } from "react-icons/ti";

const AdminDashboard = () => {
    const { userProfile, company } = useAuth();
    const [dashboardStats, setDashboardStats] = useState({
        activeJobs: null,
        completedJobs: null,
        pendingJobs: null,
        urgentJobs: null,
        totalUsers: null,
        thisWeekJobs: null
    });
    const [lastUpdated, setLastUpdated] = useState(new Date());
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { companySlug } = useParams();

    useEffect(() => {
        if (company?.id) {
            fetchDashboardStats();
        }
    }, [company]);

    const fetchDashboardStats = async () => {
        try {
            setLoading(true);
            const stats = await getDashboardStats(company.id);
            console.log(stats)
            setDashboardStats(stats);
            console.log(dashboardStats)
            console.log("_______________________________________")
            setLastUpdated(new Date());
        } catch (error) {
            console.error("Failed to fetch dashboard stats:", error);
        } finally {
            setLoading(false);
        }
    };

    const buttonNavigation = (route) => {
        navigate(`/${companySlug}/${route}`);
    };

    function capitalizeFirstLetter(string) {
        if (!string) return '';
        return string.charAt(0).toUpperCase() + string.slice(1);
    }

    const formatAusDateTime = (date) => {
        if (!date) return 'Unknown time';

        const now = new Date();
        const inputDate = new Date(date);

        if (isNaN(inputDate.getTime())) return 'Invalid date';

        const diffMs = now - inputDate;
        const diffSeconds = Math.floor(diffMs / 1000);
        const diffMinutes = Math.floor(diffSeconds / 60);
        const diffHours = Math.floor(diffMinutes / 60);
        const diffDays = Math.floor(diffHours / 24);

        const timeStr = inputDate.toLocaleTimeString('en-AU', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });

        const dayStr = inputDate.toLocaleDateString('en-AU', {
            weekday: 'long'
        });

        let relativeTime = '';

        if (diffSeconds < 30) {
            relativeTime = 'just now';
        } else if (diffSeconds < 60) {
            relativeTime = `${diffSeconds} seconds ago`;
        } else if (diffMinutes < 60) {
            relativeTime = diffMinutes === 1 ? '1 minute ago' : `${diffMinutes} minutes ago`;
        } else if (diffHours < 24) {
            relativeTime = diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
        } else if (diffDays < 7) {
            relativeTime = diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
        } else {
            const dateStr = inputDate.toLocaleDateString('en-AU', {
                day: '2-digit',
                month: 'short'
            });
            return `${timeStr} ${dateStr}`;
        }

        return `${timeStr} ${dayStr}`;
    };

    return (
        <>

            {/* Header with User Info */}
            <div className="text-center border-b border-gray-200 dark:border-zinc-700 pb-8">
                <div className="flex flex-col items-start space-y-4">
                    <div className="flex items-center space-x-4">
                        <h1 className="sm:text-3xl text-2xl font-semibold text-gray-900 dark:text-white">
                            Welcome{' '}
                            <span className="text-blue-600 dark:text-blue-400">
                                {capitalizeFirstLetter(userProfile?.full_name?.split(' ')[0] || userProfile?.username)}
                            </span>
                        </h1>
                        {/* User Type Badge */}
                        <div className="flex items-center space-x-3">
                            <div className={`px-4 py-2 rounded-lg text-sm font-medium border ${userProfile?.role === 'super_admin'
                                ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800'
                                : userProfile?.role === 'admin'
                                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800'
                                    : userProfile?.role === 'designer'
                                        ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
                                        : userProfile?.role === 'estimator'
                                            ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800'
                                            : 'bg-gray-50 dark:bg-gray-900/20 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-800'
                                }`}>
                                {userProfile?.role === 'super_admin' && '⭐ '}
                                {userProfile?.position && userProfile?.position?.toLowerCase() !== "admin" && userProfile?.position}
                                {` ${userProfile?.role}`}
                            </div>
                            {/* Online Status */}
                            <div className="flex items-center space-x-2">
                                <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                                <span className="text-sm text-gray-600 dark:text-gray-400">Online</span>
                            </div>
                        </div>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 text-lg">Dashboard Overview</p>
                </div>
            </div>

            {/* Quick Actions Grid */}
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Quick Actions</h2>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date().toLocaleDateString('en-AU', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                        })}
                    </div>
                </div>

                <div className="grid gap-6" style={{
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gridAutoRows: 'min-content'
                }}>
                    {/* New Assignment Button */}
                    {hasPermission(userProfile?.role, ["super_admin", "admin"])
                        && (
                            <button
                                onClick={() => buttonNavigation('assignment/new')}
                                className="group bg-white dark:bg-zinc-900/50 hover:bg-gray-50 dark:hover:bg-zinc-900/80 border border-gray-200 dark:border-zinc-700 hover:border-blue-300 dark:hover:border-blue-600 rounded-xl p-8 shadow-sm hover:shadow-md transition-all duration-300"
                            >
                                <div className="flex items-center space-x-4 pointer-events-none">
                                    <div className="w-12 h-12 bg-blue-600/10 dark:bg-blue-400/10 rounded-xl flex items-center justify-center group-hover:bg-blue-600/20 dark:group-hover:bg-blue-400/20 transition-colors flex-shrink-0">
                                        <FaPlus className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <div className="text-left">
                                        <h3 className="font-semibold text-gray-900 dark:text-white text-base">New Assignment</h3>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Create and assign new jobs</p>
                                    </div>
                                </div>
                            </button>
                        )}

                    {/* My Assignments Button */}
                    {hasPermission(userProfile?.role, ["designer", "estimator"]) && (
                        <button
                            onClick={() => buttonNavigation('assignment/my-assignments')}
                            className="group bg-white dark:bg-zinc-900/50 hover:bg-gray-50 dark:hover:bg-zinc-900/80 border border-gray-200 dark:border-zinc-700 hover:border-emerald-300 dark:hover:border-emerald-600 rounded-xl p-8 shadow-sm hover:shadow-md transition-all duration-300"
                        >
                            <div className="flex items-start space-x-4 pointer-events-none">
                                <div className="w-12 h-12 bg-emerald-600/10 dark:bg-emerald-400/10 rounded-xl flex items-center justify-center group-hover:bg-emerald-600/20 dark:group-hover:bg-emerald-400/20 transition-colors flex-shrink-0">
                                    <FaBoxOpen className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                                </div>
                                <div className="text-left">
                                    <h3 className="font-semibold text-gray-900 dark:text-white text-base">My Assignments</h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Monitor your assignments</p>
                                </div>
                            </div>
                        </button>
                    )}

                    {/* My Deliveries Button */}
                    {hasPermission(userProfile?.role, ["driver"]) && (
                        <button
                            onClick={() => buttonNavigation('my-deliveries')}
                            className="group bg-white dark:bg-zinc-900/50 hover:bg-gray-50 dark:hover:bg-zinc-900/80 border border-gray-200 dark:border-zinc-700 hover:border-emerald-300 dark:hover:border-emerald-600 rounded-xl p-8 shadow-sm hover:shadow-md transition-all duration-300"
                        >
                            <div className="flex items-start space-x-4 pointer-events-none">
                                <div className="w-12 h-12 bg-emerald-600/10 dark:bg-emerald-400/10 rounded-xl flex items-center justify-center group-hover:bg-emerald-600/20 dark:group-hover:bg-emerald-400/20 transition-colors flex-shrink-0">
                                    <Truck className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                                </div>
                                <div className="text-left">
                                    <h3 className="font-semibold text-gray-900 dark:text-white text-base">My Deliveries</h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Monitor your deliveries</p>
                                </div>
                            </div>
                        </button>
                    )}

                    {/* My Deliveries Button */}
                    {hasPermission(userProfile?.role, ["driver"]) && (
                        <button
                            onClick={() => buttonNavigation('my-deliveries/completed')}
                            className="group bg-white dark:bg-zinc-900/50 hover:bg-gray-50 dark:hover:bg-zinc-900/80 border border-gray-200 dark:border-zinc-700 hover:border-emerald-300 dark:hover:border-emerald-600 rounded-xl p-8 shadow-sm hover:shadow-md transition-all duration-300"
                        >
                            <div className="flex items-start space-x-4 pointer-events-none">
                                <div className="w-12 h-12 bg-emerald-600/10 dark:bg-emerald-400/10 rounded-xl flex items-center justify-center group-hover:bg-emerald-600/20 dark:group-hover:bg-emerald-400/20 transition-colors flex-shrink-0">
                                    <FiCheckCircle className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                                </div>
                                <div className="text-left">
                                    <h3 className="font-semibold text-gray-900 dark:text-white text-base">Completed Deliveries</h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Monitor your old deliveries</p>
                                </div>
                            </div>
                        </button>
                    )}

                    {/* Track Assignments Button */}
                    {hasPermission(userProfile?.role, ["super_admin", "admin", "designer", "estimator"]) && (
                        <button
                            onClick={() => buttonNavigation('assignment/list')}
                            className="group bg-white dark:bg-zinc-900/50 hover:bg-gray-50 dark:hover:bg-zinc-900/80 border border-gray-200 dark:border-zinc-700 hover:border-emerald-300 dark:hover:border-emerald-600 rounded-xl p-8 shadow-sm hover:shadow-md transition-all duration-300"
                        >
                            <div className="flex items-start space-x-4 pointer-events-none">
                                <div className="w-12 h-12 bg-emerald-600/10 dark:bg-emerald-400/10 rounded-xl flex items-center justify-center group-hover:bg-emerald-600/20 dark:group-hover:bg-emerald-400/20 transition-colors flex-shrink-0">
                                    <FaBoxesStacked className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                                </div>
                                <div className="text-left">
                                    <h3 className="font-semibold text-gray-900 dark:text-white text-base">Track Assignments</h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Monitor all active jobs</p>
                                </div>
                            </div>
                        </button>
                    )}

                    {/* Manage Users Button */}
                    {hasPermission(userProfile?.role, ["super_admin", "admin", "designer", "estimator", "driver"]) && (
                        <button
                            onClick={() => buttonNavigation('users')}
                            className="group bg-white dark:bg-zinc-900/50 hover:bg-gray-50 dark:hover:bg-zinc-900/80 border border-gray-200 dark:border-zinc-700 hover:border-purple-300 dark:hover:border-purple-600 rounded-xl p-8 shadow-sm hover:shadow-md transition-all duration-300"
                        >
                            <div className="flex items-center space-x-4 pointer-events-none">
                                <div className="w-12 h-12 bg-purple-600/10 dark:bg-purple-400/10 rounded-xl flex items-center justify-center group-hover:bg-purple-600/20 dark:group-hover:bg-purple-400/20 transition-colors flex-shrink-0">
                                    <MdManageAccounts className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                                </div>
                                <div className="text-left">
                                    <h3 className="font-semibold text-gray-900 dark:text-white text-base">Users</h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">See{hasPermission(userProfile?.role, ["super_admin", "admin"]) && " & manage"} the users and contacts</p>
                                </div>
                            </div>
                        </button>
                    )}

                    {/* Schedule Button */}
                    {hasPermission(userProfile?.role, ["super_admin", "admin", "designer", "estimator"]) &&
                        <button
                            onClick={() => buttonNavigation('schedule')}
                            className="group bg-white dark:bg-zinc-900/50 hover:bg-gray-50 dark:hover:bg-zinc-900/80 border border-gray-200 dark:border-zinc-700 hover:border-orange-300 dark:hover:border-orange-600 rounded-xl p-8 shadow-sm hover:shadow-md transition-all duration-300"
                        >
                            <div className="flex items-center space-x-4 pointer-events-none">
                                <div className="w-12 h-12 bg-orange-600/10 dark:bg-orange-400/10 rounded-xl flex items-center justify-center group-hover:bg-orange-600/20 dark:group-hover:bg-orange-400/20 transition-colors flex-shrink-0">
                                    <RiCalendarScheduleFill className="!w-6 !h-6 text-orange-600 dark:text-orange-400" />
                                </div>
                                <div className="text-left">
                                    <h3 className="font-semibold text-gray-900 dark:text-white text-base">Schedule</h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Plan and organize tasks</p>
                                </div>
                            </div>
                        </button>
                    }
                </div>
            </div>

            {/* Session Information */}
            <div className="bg-gray-50 dark:bg-zinc-900/50 rounded-xl p-8 border border-gray-200 dark:border-zinc-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                    Session Information
                </h3>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* User Information */}
                    <div className="bg-white dark:bg-zinc-800 rounded-xl p-6 border border-gray-200 dark:border-zinc-700">
                        <div className="flex items-center justify-between mb-4">
                            <h4 className="font-semibold text-gray-900 dark:text-white">User Details</h4>
                            <div className={`px-3 py-1 rounded-lg text-xs font-medium border ${userProfile?.role === 'admin'
                                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800'
                                : userProfile?.role === 'designer'
                                    ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
                                    : 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800'
                                }`}>
                                {userProfile?.role?.toUpperCase() || 'USER'}
                            </div>
                        </div>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center py-2">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Username</span>
                                <span className="font-medium text-gray-900 dark:text-white">
                                    {userProfile?.username || userProfile?.full_name}
                                </span>
                            </div>
                            <div className="flex justify-between items-center py-2">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Email</span>
                                <span className="font-medium text-gray-900 dark:text-white text-sm">
                                    {userProfile?.email}
                                </span>
                            </div>
                            <div className="flex justify-between items-center py-2">
                                <span className="text-sm text-gray-600 dark:text-gray-400">User ID</span>
                                <span className="font-mono text-xs text-gray-900 dark:text-white truncate max-w-32">
                                    {userProfile?.id}
                                </span>
                            </div>
                            {userProfile?.role === 'super_admin' && (
                                <div className="flex justify-between items-center py-2">
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Role</span>
                                    <span className="text-xs bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300 px-3 py-1 rounded-lg font-medium border border-amber-200 dark:border-amber-800">
                                        Super Admin
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Company Information */}
                    <div className="bg-white dark:bg-zinc-800 rounded-xl p-6 border border-gray-200 dark:border-zinc-700">
                        <div className="flex items-center justify-between mb-4">
                            <h4 className="font-semibold text-gray-900 dark:text-white">Company Details</h4>
                            <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                        </div>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center py-2">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Company</span>
                                <span className="font-medium text-gray-900 dark:text-white">{company?.name}</span>
                            </div>
                            <div className="flex justify-between items-center py-2">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Slug</span>
                                <span className="font-mono text-sm text-gray-900 dark:text-white">{company?.slug}</span>
                            </div>
                            <div className="flex justify-between items-center py-2">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Timezone</span>
                                <span className="font-medium text-gray-900 dark:text-white text-sm">{company?.timezone}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AdminDashboard;